'use client';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RawPlacementApiResponse } from '@campaign-manager/core/model';
import usePlacements from '@campaign-manager/feature/hook/repo/rmt-api/usePlacements';

import { JsonViewer } from '../JsonViewer';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxHeight: '160vh',
    overflow: 'hidden',
    minWidth: '700px',
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    paddingLeft: theme.spacing(2),
    '& fieldset': {
      border: 'none',
    },
  },
}));

const PlacementList = styled(List)(({ theme }) => ({
  maxHeight: '600px',
  overflowY: 'auto',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PlacementItem = styled(ListItemButton)<{ selected?: boolean }>(({ theme, selected }) => ({
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const JsonViewerContainer = styled(Box)(({ theme }) => ({
  minHeight: '600px',
  maxHeight: '800px',
  overflow: 'auto',
  padding: theme.spacing(2),
}));

interface PlacementSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function PlacementSearchDialog({ open, onClose }: PlacementSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedPlacement, setSelectedPlacement] = useState<RawPlacementApiResponse | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const searchFieldRef = useRef<HTMLInputElement>(null);

  // Fetch placements data
  const { data: placements = [], error, loading } = usePlacements({});

  // Filter placements based on search query
  const filteredPlacements = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return placements;

    return placements.filter((placement) => {
      const searchTargets = [
        placement.name.toLowerCase(),
        placement.id.toLowerCase(),
        placement.description?.toLowerCase() || '',
        placement.group?.toLowerCase() || '',
      ];
      return searchTargets.some((target) => target.includes(query));
    });
  }, [placements, searchQuery]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setSelectedIndex(0);
    setSelectedPlacement(null);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, filteredPlacements.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (filteredPlacements[selectedIndex]) {
            setSelectedPlacement(filteredPlacements[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (selectedPlacement) {
            setSelectedPlacement(null);
          } else {
            onClose();
          }
          break;
      }
    },
    [filteredPlacements, selectedIndex, selectedPlacement, onClose],
  );

  // Handle placement selection
  const handlePlacementSelect = useCallback((placement: RawPlacementApiResponse) => {
    setSelectedPlacement(placement);
  }, []);

  // Reset state when dialog opens and focus search field
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedIndex(0);
      setSelectedPlacement(null);
      // Focus the search field after a short delay to ensure the dialog is fully rendered
      setTimeout(() => {
        searchFieldRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedItem = itemRefs.current[selectedIndex];
    if (selectedItem && open && !selectedPlacement) {
      selectedItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex, open, selectedPlacement]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {selectedPlacement ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Placement Details</Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {selectedPlacement.name} ({selectedPlacement.id})
            </Typography>
          </Box>
        ) : (
          'Search Placements'
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {!selectedPlacement && (
          <>
            {/* Search Field */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <SearchField
                fullWidth
                placeholder="Search by name or ID..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoFocus
                inputRef={searchFieldRef}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
                size="medium"
                sx={{ my: 2 }}
              />
            </Box>

            {/* Loading/Error States */}
            {loading && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Loading placements...
                </Typography>
              </Box>
            )}

            {error && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  color="error"
                >
                  Error loading placements: {error.message}
                </Typography>
              </Box>
            )}

            {/* Placement List */}
            {!loading && !error && (
              <>
                {filteredPlacements.length > 0 ? (
                  <PlacementList>
                    {filteredPlacements.map((placement, index) => (
                      <PlacementItem
                        key={placement.id}
                        ref={(el) => {
                          itemRefs.current[index] = el;
                        }}
                        selected={index === selectedIndex}
                        onClick={() => handlePlacementSelect(placement)}
                      >
                        <ListItemText
                          primary={placement.name}
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                ID: {placement.id}
                              </Typography>
                              {placement.description && (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  {placement.description}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                Status: {placement.status} | Ads: {placement.ads_count}
                              </Typography>
                            </Box>
                          }
                        />
                      </PlacementItem>
                    ))}
                  </PlacementList>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {searchQuery
                        ? 'No placements found matching your search'
                        : 'No placements available'}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Help Text */}
            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Use ↑↓ to navigate, Enter to select, Esc to close
              </Typography>
            </Box>
          </>
        )}

        {/* Selected Placement JSON View */}
        {selectedPlacement && (
          <JsonViewerContainer>
            <JsonViewer
              data={selectedPlacement}
              maxInitialDepth={3}
              showCopyButton={true}
              sx={{ maxHeight: 'unset' }}
            />
          </JsonViewerContainer>
        )}
      </DialogContent>
    </StyledDialog>
  );
}
